import {
  isComplaintBreach,
  formatMinutes,
  getCleanerClockDotStatus,
  computeIdleMs,
  resolveIdleThresholdMinutes,
} from './cleanerActivityUtils'

const ATTENTION_TYPES = {
  SLA_BREACH: 'sla_breach',
  OPEN_WHILE_OUT: 'open_while_out',
  IDLE: 'idle',
  LATE: 'late',
}

const SEVERITY = {
  [ATTENTION_TYPES.SLA_BREACH]: 4,
  [ATTENTION_TYPES.OPEN_WHILE_OUT]: 3,
  [ATTENTION_TYPES.IDLE]: 2,
  [ATTENTION_TYPES.LATE]: 1,
}

const ATTENTION_ICONS = {
  [ATTENTION_TYPES.SLA_BREACH]: '⚠',
  [ATTENTION_TYPES.OPEN_WHILE_OUT]: '○',
  [ATTENTION_TYPES.IDLE]: '⏸',
  [ATTENTION_TYPES.LATE]: '⏰',
}

export function attentionTypeIcon(type) {
  return ATTENTION_ICONS[type] || '•'
}

/**
 * Build sorted attention alerts for the live strip.
 * @param {Array<Object>} cleaners
 * @param {{ slaMinutes?: number|null, shiftStart?: Date|null, nowMs?: number, idleThresholdMinutes?: number }} options
 */
export function buildAttentionItems(
  cleaners,
  {
    slaMinutes = null,
    shiftStart = null,
    nowMs = Date.now(),
    idleThresholdMinutes = null,
  } = {},
) {
  const items = []
  const thresholdMs = resolveIdleThresholdMinutes(idleThresholdMinutes) * 60 * 1000

  for (const cleaner of cleaners) {
    const employeeId = cleaner.employeeId
    const username = cleaner.username || `#${employeeId}`
    const metrics = cleaner.metrics || {}
    const isClockedIn = metrics.isClockedIn
    const complaints = cleaner.complaints || []
    const openCount = metrics.openComplaints || 0

    const breachCount = complaints.filter(
      (complaint) => !complaint.closeTime && isComplaintBreach(complaint, slaMinutes, nowMs),
    ).length
    if (breachCount > 0) {
      items.push({
        key: `breach-${employeeId}`,
        type: ATTENTION_TYPES.SLA_BREACH,
        employeeId,
        username,
        count: breachCount,
        severity: SEVERITY[ATTENTION_TYPES.SLA_BREACH],
      })
    }

    if (!isClockedIn && openCount > 0) {
      items.push({
        key: `out-open-${employeeId}`,
        type: ATTENTION_TYPES.OPEN_WHILE_OUT,
        employeeId,
        username,
        count: openCount,
        severity: SEVERITY[ATTENTION_TYPES.OPEN_WHILE_OUT],
      })
    }

    if (isClockedIn && openCount === 0) {
      const idleMs = computeIdleMs(cleaner, nowMs)
      if (idleMs >= thresholdMs) {
        items.push({
          key: `idle-${employeeId}`,
          type: ATTENTION_TYPES.IDLE,
          employeeId,
          username,
          idleMinutes: Math.max(1, Math.round(idleMs / 60000)),
          severity: SEVERITY[ATTENTION_TYPES.IDLE],
        })
      }
    }

    if (getCleanerClockDotStatus(cleaner, shiftStart) === 'late') {
      items.push({
        key: `late-${employeeId}`,
        type: ATTENTION_TYPES.LATE,
        employeeId,
        username,
        severity: SEVERITY[ATTENTION_TYPES.LATE],
      })
    }
  }

  return items.sort((a, b) => {
    if (b.severity !== a.severity) return b.severity - a.severity
    return String(a.username).localeCompare(String(b.username))
  })
}

/** @param {(key: string, params?: Object) => string} t */
export function attentionItemLabel(item, t) {
  const name = item.username
  switch (item.type) {
    case ATTENTION_TYPES.SLA_BREACH:
      return item.count > 1
        ? t('ca_attention_sla_breach_many', { name, count: item.count })
        : t('ca_attention_sla_breach_one', { name })
    case ATTENTION_TYPES.OPEN_WHILE_OUT:
      return item.count > 1
        ? t('ca_attention_open_while_out_many', { name, count: item.count })
        : t('ca_attention_open_while_out_one', { name })
    case ATTENTION_TYPES.IDLE:
      return t('ca_attention_idle', {
        name,
        duration: formatMinutes(item.idleMinutes, { compact: true }),
      })
    case ATTENTION_TYPES.LATE:
      return t('ca_attention_late', { name })
    default:
      return name
  }
}
